import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const getAuthToken = () => {
    const adminData = sessionStorage.getItem('adminkhadamat');
    if (adminData) {
        try {
            const parsed = JSON.parse(adminData);
            // البحث عن التوكن في أماكن مختلفة
            if (parsed.data?.token?.access_token) {
              const token = String(parsed.data.token.access_token);
              return token;
            }
            if (parsed.data?.access_token) return parsed.data.access_token;
            if (parsed.data?.token) return parsed.data.token;
            if (parsed.token) return parsed.token;
            if (parsed.result?.token) return parsed.result.token;
            if (parsed.user?.token) return parsed.user.token;
            return null;
        } catch {
            return null;
        }
    }
    return null;
};

// Fetch all stores
export const fetchStores = createAsyncThunk(
    'adminStores/fetchStores',
    async ({ page = 1, name = '', category_id = '' }, { rejectWithValue, signal }) => {
        try {
            const token = getAuthToken();
            if (!token) {
                return rejectWithValue('غير مسجل دخول');
            }

            const params = { page };
            if (name && name.trim()) params.name = name.trim();
            if (category_id) params.category_id = category_id;

            const response = await axios.get(`${BASE_URL}/admin/stores`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
                params,
                signal
            });

            return response.data;
        } catch (error) {
            if (error.name === 'AbortError') {
                return rejectWithValue('تم إلغاء الطلب');
            }
            return rejectWithValue(error.response?.data?.message || 'فشل في جلب المتاجر');
        }
    }
);

// Fetch store details
export const fetchStoreDetails = createAsyncThunk(
    'adminStores/fetchStoreDetails',
    async (storeId, { rejectWithValue }) => {
        try {
            const token = getAuthToken();
            const response = await axios.get(`${BASE_URL}/admin/stores/${storeId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });

            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'فشل في جلب تفاصيل المتجر');
        }
    }
);

// Toggle store ban status
export const toggleStoreBan = createAsyncThunk(
    'adminStores/toggleStoreBan',
    async ({ storeId, status }, { rejectWithValue }) => {
        try {
            const token = getAuthToken();
            const response = await axios.post(
                `${BASE_URL}/admin/stores/${storeId}/ban?ban=${status}`,
                {},
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept-Language': 'ar',
                        'lang': 'ar',
                    },
                }
            );

            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'فشل في العملية');
        }
    }
);

// Toggle store activation status
export const toggleStoreStatus = createAsyncThunk(
    'adminStores/toggleStoreStatus',
    async ({ storeId, status }, { rejectWithValue }) => {
        try {
            const token = getAuthToken();
            const response = await axios.post(
                `${BASE_URL}/admin/stores/${storeId}/status?status=${status}`,
                {},
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept-Language': 'ar',
                        'lang': 'ar',
                    },
                }
            );

            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'فشل في العملية');
        }
    }
);

// Fetch store branches
export const fetchStoreBranches = createAsyncThunk(
    'adminStores/fetchStoreBranches',
    async ({ storeId, page = 1 }, { rejectWithValue }) => {
        try {
            const token = getAuthToken();
            const response = await axios.get(`${BASE_URL}/admin/stores/${storeId}/branches`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
                params: { page }
            });

            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'فشل في جلب الفروع');
        }
    }
);

// Fetch branch details
export const fetchBranchDetails = createAsyncThunk(
    'adminStores/fetchBranchDetails',
    async ({ storeId, branchId }, { rejectWithValue }) => {
        try {
            const token = getAuthToken();
            const response = await axios.get(`${BASE_URL}/admin/stores/${storeId}/branches/${branchId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });

            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'فشل في جلب تفاصيل الفرع');
        }
    }
);

const adminStoresSlice = createSlice({
    name: 'adminStores',
    initialState: {
        stores: [],
        storeDetails: null,
        branches: [],
        branchDetails: null,
        pagination: null,
        branchesPagination: null,
        loading: false,
        detailsLoading: false,
        branchesLoading: false,
        branchDetailsLoading: false,
        updating: false,
        error: null,
        success: null,
    },
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSuccess: (state) => {
            state.success = null;
        },
        clearStoreDetails: (state) => {
            state.storeDetails = null;
        },
        clearBranchDetails: (state) => {
            state.branchDetails = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch Stores
            .addCase(fetchStores.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchStores.fulfilled, (state, action) => {
                state.loading = false;
                const storesData = action.payload.data?.data || action.payload.data;
                state.stores = Array.isArray(storesData) ? storesData : [];
                state.pagination = action.payload.data?.meta || action.payload.pagination;
            })
            .addCase(fetchStores.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Fetch Store Details
            .addCase(fetchStoreDetails.pending, (state) => {
                state.detailsLoading = true;
                state.error = null;
            })
            .addCase(fetchStoreDetails.fulfilled, (state, action) => {
                state.detailsLoading = false;
                state.storeDetails = action.payload.data;
            })
            .addCase(fetchStoreDetails.rejected, (state, action) => {
                state.detailsLoading = false;
                state.error = action.payload;
            })

            // Toggle Store Ban
            .addCase(toggleStoreBan.pending, (state) => {
                state.updating = true;
                state.error = null;
            })
            .addCase(toggleStoreBan.fulfilled, (state, action) => {
                state.updating = false;
                state.success = action.payload.message || 'تم تحديث حالة الحظر بنجاح';
            })
            .addCase(toggleStoreBan.rejected, (state, action) => {
                state.updating = false;
                state.error = action.payload;
            })

            // Toggle Store Status
            .addCase(toggleStoreStatus.pending, (state) => {
                state.updating = true;
                state.error = null;
            })
            .addCase(toggleStoreStatus.fulfilled, (state, action) => {
                state.updating = false;
                state.success = action.payload.message || 'تم تحديث حالة التفعيل بنجاح';
            })
            .addCase(toggleStoreStatus.rejected, (state, action) => {
                state.updating = false;
                state.error = action.payload;
            })

            // Fetch Store Branches
            .addCase(fetchStoreBranches.pending, (state) => {
                state.branchesLoading = true;
                state.error = null;
            })
            .addCase(fetchStoreBranches.fulfilled, (state, action) => {
                state.branchesLoading = false;
                const branchesData = action.payload.data?.data || action.payload.data;
                state.branches = Array.isArray(branchesData) ? branchesData : [];
                state.branchesPagination = action.payload.data?.meta || action.payload.pagination;
            })
            .addCase(fetchStoreBranches.rejected, (state, action) => {
                state.branchesLoading = false;
                state.error = action.payload;
            })

            // Fetch Branch Details
            .addCase(fetchBranchDetails.pending, (state) => {
                state.branchDetailsLoading = true;
                state.error = null;
            })
            .addCase(fetchBranchDetails.fulfilled, (state, action) => {
                state.branchDetailsLoading = false;
                state.branchDetails = action.payload.data;
            })
            .addCase(fetchBranchDetails.rejected, (state, action) => {
                state.branchDetailsLoading = false;
                state.error = action.payload;
            });
    },
});

export const { clearError, clearSuccess, clearStoreDetails, clearBranchDetails } = adminStoresSlice.actions;
export default adminStoresSlice.reducer;

